import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { withUploadToken } from '../../utils/fileUrl';
import {
  Alert,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import FemaleIcon from '@mui/icons-material/Female';
import MaleIcon from '@mui/icons-material/Male';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Horse } from '@phosphor-icons/react';
import api, { apiBaseURL } from '../../services/api';
import {
  SMART_COLORS as COLORS,
  smartCardSx as cardSx,
  smartPageSx,
  smartTableContainerSx,
  smartTableHeadSx,
  smartTableSx,
} from '../../theme/smartStableTheme';
import { formatDateDisplay } from '../../utils/dateUtils';

const COPY = {
  ar: {
    title: 'خيلي', subtitle: 'كل الخيل المسجّلة باسمك — عرض فقط.',
    search: 'ابحث بالاسم أو رقم الشريحة', gender: 'الجنس', all: 'الكل',
    mares: 'إناث', stallions: 'ذكور', records: 'سجل',
    totalHorses: 'إجمالي الخيل', withRfid: 'لها شريحة', shown: 'معروض',
    colHorse: 'الخيل', colGender: 'الجنس', colDob: 'تاريخ الميلاد', colAge: 'العمر',
    colBreed: 'السلالة', colColor: 'اللون', colLocation: 'السكن', colActions: 'إجراءات',
    view: 'عرض التفاصيل', years: 'سنة',
    noData: 'لا توجد خيل', noLink: 'هذا الحساب غير مربوط بمالك.',
  },
  en: {
    title: 'My Horses', subtitle: 'All horses registered under your name — read-only.',
    search: 'Search by name or RFID', gender: 'Gender', all: 'All',
    mares: 'Mares', stallions: 'Stallions', records: 'records',
    totalHorses: 'Total horses', withRfid: 'With RFID', shown: 'shown',
    colHorse: 'Horse', colGender: 'Gender', colDob: 'DOB', colAge: 'Age',
    colBreed: 'Breed', colColor: 'Color', colLocation: 'Accommodation', colActions: 'Actions',
    view: 'View details', years: 'yrs',
    noData: 'No horses', noLink: 'This account is not linked to an owner profile.',
  },
};

const formatNumber = (value) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Number(value || 0));

function getImageSrc(image) {
  if (!image) return undefined;
  if (image.startsWith('data') || image.startsWith('http')) return image;
  return withUploadToken(`${apiBaseURL}/uploads/${image}`);
}
function getHorseName(horse, lang) {
  return (lang === 'ar' ? horse.name_arabic || horse.name : horse.name || horse.name_arabic) || '-';
}
function pickLabel(horse, enKey, arKey, lang) {
  return (lang === 'ar' ? horse[arKey] || horse[enKey] : horse[enKey] || horse[arKey]) || '-';
}
function calcAge(dob, lang, years) {
  if (!dob) return '-';
  const date = new Date(dob);
  if (Number.isNaN(date.getTime())) return '-';
  const today = new Date();
  let age = today.getFullYear() - date.getFullYear();
  const m = today.getMonth() - date.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < date.getDate())) age -= 1;
  return `${Math.max(age, 0)} ${years}`;
}

function ResponsiveGrid({ children, min = 220, sx }) {
  return <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`, gap: 2, alignItems: 'stretch', ...sx }}>{children}</Box>;
}
function MetricCard({ icon, title, value, helper, color = COLORS.gold, soft = COLORS.goldSoft }) {
  return (
    <Paper elevation={0} sx={{ ...cardSx, p: 2.2, minHeight: 118 }}>
      <Stack direction="row" spacing={1.6} alignItems="center">
        <Avatar sx={{ bgcolor: soft, color, width: 48, height: 48 }}>{icon}</Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ color, fontWeight: 900 }}>{title}</Typography>
          <Typography variant="h5" sx={{ color: COLORS.text, fontWeight: 900, mt: 0.3 }}>{value}</Typography>
          {helper ? <Typography variant="caption" sx={{ color: COLORS.muted }}>{helper}</Typography> : null}
        </Box>
      </Stack>
    </Paper>
  );
}

const ClientHorseList = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang = i18n.language === 'ar' ? 'ar' : 'en';
  const text = COPY[lang];
  const ownerId = localStorage.getItem('owner_id');

  const [horses, setHorses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [genderFilter, setGenderFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const genders = useMemo(() => ({ S: text.stallions, M: text.mares }), [text]);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      if (!ownerId || ownerId === 'null' || ownerId === 'undefined') {
        setError(text.noLink);
        setLoading(false);
        return;
      }
      try {
        const { data } = await api.get(`/owners/${ownerId}/horses-with-details`);
        if (alive) setHorses(Array.isArray(data) ? data : []);
      } catch {
        if (alive) setError(text.noData);
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => { alive = false; };
  }, [ownerId, text.noData, text.noLink]);

  const stats = useMemo(() => ({
    stallions: horses.filter((h) => h.gender === 'S').length,
    mares: horses.filter((h) => h.gender === 'M').length,
    withRfid: horses.filter((h) => h.rfid_chip_number).length,
  }), [horses]);

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return horses.filter((h) => {
      if (genderFilter && h.gender !== genderFilter) return false;
      if (!q) return true;
      return [h.name, h.name_arabic, h.rfid_chip_number, h.breed_english, h.breed_arabic, h.color_english, h.color_arabic, h.stable_name, h.room_name]
        .some((v) => String(v || '').toLowerCase().includes(q));
    });
  }, [horses, searchTerm, genderFilter]);

  const paged = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage]
  );

  if (loading) {
    return (
      <Box sx={{ ...smartPageSx, display: 'grid', placeItems: 'center' }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box dir={lang === 'ar' ? 'rtl' : 'ltr'} sx={{ ...smartPageSx, px: { xs: 2, md: 3 }, py: 3 }}>
      <Box sx={{ maxWidth: 1480, mx: 'auto' }}>
        <Paper elevation={0} sx={{ ...cardSx, p: { xs: 2, md: 3 }, mb: 2.5, background: 'linear-gradient(135deg, #fffdf8 0%, #f4efe5 100%)' }}>
          <Typography variant="h4" sx={{ color: COLORS.text, fontWeight: 900 }}>{text.title}</Typography>
          <Typography sx={{ color: COLORS.muted, mt: 0.6 }}>{text.subtitle}</Typography>
        </Paper>

        {error ? <Alert severity="warning" sx={{ mb: 2.5 }}>{error}</Alert> : null}

        <ResponsiveGrid min={220} sx={{ mb: 2.5 }}>
          <MetricCard icon={<Horse size={26} weight="fill" />} title={text.totalHorses} value={formatNumber(horses.length)} helper={`${formatNumber(filtered.length)} ${text.shown}`} />
          <MetricCard icon={<MaleIcon />} title={text.stallions} value={formatNumber(stats.stallions)} color={COLORS.blue} soft={COLORS.blueSoft} />
          <MetricCard icon={<FemaleIcon />} title={text.mares} value={formatNumber(stats.mares)} color={COLORS.rose} soft={COLORS.roseSoft} />
          <MetricCard icon={<Horse size={26} weight="fill" />} title={text.withRfid} value={formatNumber(stats.withRfid)} color={COLORS.green} soft={COLORS.greenSoft} />
        </ResponsiveGrid>

        <Paper elevation={0} sx={{ ...cardSx, overflow: 'visible' }}>
          <Box sx={{ p: 2, borderBottom: `1px solid ${COLORS.line}` }}>
            <Stack direction={{ xs: 'column', md: 'row' }} gap={1.25} justifyContent="space-between">
              <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.25}>
                <TextField
                  size="small"
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
                  placeholder={text.search}
                  sx={{ minWidth: { xs: '100%', sm: 340 } }}
                  InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }}
                />
                <TextField
                  select size="small" label={text.gender} value={genderFilter}
                  onChange={(e) => { setGenderFilter(e.target.value); setPage(0); }}
                  sx={{ minWidth: 150 }}
                >
                  <MenuItem value="">{text.all}</MenuItem>
                  <MenuItem value="S">{genders.S}</MenuItem>
                  <MenuItem value="M">{genders.M}</MenuItem>
                </TextField>
              </Stack>
              <Chip label={`${formatNumber(filtered.length)} ${text.records}`} sx={{ bgcolor: COLORS.goldSoft, color: COLORS.text, fontWeight: 800, alignSelf: { xs: 'flex-start', md: 'center' } }} />
            </Stack>
          </Box>

          <TableContainer sx={smartTableContainerSx}>
            <Table size="small" sx={smartTableSx}>
              <TableHead sx={smartTableHeadSx}>
                <TableRow>
                  <TableCell width={70}>#</TableCell>
                  <TableCell>{text.colHorse}</TableCell>
                  <TableCell align="center" width={120}>{text.colGender}</TableCell>
                  <TableCell align="center" width={125}>{text.colDob}</TableCell>
                  <TableCell align="center" width={90}>{text.colAge}</TableCell>
                  <TableCell>{text.colBreed}</TableCell>
                  <TableCell>{text.colColor}</TableCell>
                  <TableCell>{text.colLocation}</TableCell>
                  <TableCell align="center" width={90}>{text.colActions}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paged.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 6, color: COLORS.muted }}>{text.noData}</TableCell>
                  </TableRow>
                ) : (
                  paged.map((horse, index) => (
                    <TableRow key={horse.horse_id} hover sx={{ '& .MuiTableCell-root': { borderBottom: `1px solid ${COLORS.line}` }, '&:hover': { bgcolor: '#fffaf0' } }}>
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell sx={{ minWidth: 280 }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '46px minmax(0, 1fr)', columnGap: 1.4, alignItems: 'center', width: '100%', textAlign: lang === 'ar' ? 'right' : 'left' }}>
                          <Avatar src={getImageSrc(horse.image)} alt={horse.name} sx={{ width: 46, height: 46, bgcolor: COLORS.goldSoft, color: COLORS.gold, fontWeight: 900, justifySelf: 'center' }}>
                            {getHorseName(horse, lang).charAt(0).toUpperCase()}
                          </Avatar>
                          <Box sx={{ minWidth: 0, width: '100%' }}>
                            <Typography
                              noWrap
                              onClick={() => navigate(`/my-horses/${horse.horse_id}`)}
                              sx={{ color: COLORS.text, fontWeight: 900, cursor: 'pointer', display: 'block', width: '100%', textAlign: lang === 'ar' ? 'right' : 'left', direction: lang === 'ar' ? 'rtl' : 'ltr' }}
                            >
                              {getHorseName(horse, lang)}
                            </Typography>
                            <Stack direction="row" alignItems="center" spacing={0.6} sx={{ mt: 0.2, justifyContent: lang === 'ar' ? 'flex-end' : 'flex-start', width: '100%' }}>
                              <Typography component="span" variant="caption" sx={{ color: COLORS.muted, fontWeight: 800 }}>RFID</Typography>
                              <Typography component="span" variant="caption" sx={{ color: COLORS.muted, direction: 'ltr' }}>{horse.rfid_chip_number || '-'}</Typography>
                            </Stack>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Chip size="small" icon={horse.gender === 'S' ? <MaleIcon /> : <FemaleIcon />} label={genders[horse.gender] || '-'} sx={{ bgcolor: horse.gender === 'S' ? COLORS.blueSoft : COLORS.roseSoft, color: COLORS.text, fontWeight: 800 }} />
                      </TableCell>
                      <TableCell align="center"><span className="date-ltr">{formatDateDisplay(horse.date_of_birth)}</span></TableCell>
                      <TableCell align="center">{calcAge(horse.date_of_birth, lang, text.years)}</TableCell>
                      <TableCell>{pickLabel(horse, 'breed_english', 'breed_arabic', lang)}</TableCell>
                      <TableCell>{pickLabel(horse, 'color_english', 'color_arabic', lang)}</TableCell>
                      <TableCell>{[horse.stable_name, horse.room_name].filter(Boolean).join(' - ') || '-'}</TableCell>
                      <TableCell align="center">
                        <Tooltip title={text.view}>
                          <IconButton onClick={() => navigate(`/my-horses/${horse.horse_id}`)} sx={{ color: COLORS.blue }}>
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(_e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage={lang === 'ar' ? 'صفوف لكل صفحة' : 'Rows per page'}
          />
        </Paper>
      </Box>
    </Box>
  );
};

export default ClientHorseList;
